import { Component, h, primitiveNodeTypes, useMemo, useRef } from "@lukekaalim/act";
import { html } from "@lukekaalim/act-web";

import * as three from 'three';
import { useJeffect, useLogger } from "@kosmod/kit";
import { first } from "rxjs";
import { a3, ThreeJSRoot } from "@lukekaalim/act-three";


import classes from './Snapshot.module.css';
import { Graphics3DSetup, renderBeat } from "../../core";
import { ObjectClone } from "../Objects/ObjectClone";

export type SnapshotProps = {
  object: null | three.Object3D,

  showBoundingBox?: boolean,
  graphics: Graphics3DSetup,
};

export const Snapshot: Component<SnapshotProps> = ({
  object,
  children,
  graphics, showBoundingBox
}) => {
  const logger = useLogger('Snapshot');

  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const previewSceneRef = useRef<three.Scene | null>(null);
  const previewCameraRef = useRef<three.OrthographicCamera | null>(null);

  if (!object)
    return null;

  const render = renderBeat.useObservable();

  const { box, size } = useMemo(() => {
    const clone = object && object.clone();
    if (clone) {
      clone.position.set(0, 0, 0);
    }
    const box = clone && new three.Box3().setFromObject(clone);
    const size = box && box.getSize(new three.Vector3());

    return { box, size };
  }, [object])


  useJeffect((j) => {
    const renderer = graphics.renderer.get();
    const sourceCanvas = graphics.canvas.get();

    const destinationCanvas = previewCanvasRef.get();
    const scene = previewSceneRef.get();
    const camera = previewCameraRef.get();

    if (!renderer || !sourceCanvas || !destinationCanvas || !scene || !camera)
      return;

    const draw2D = destinationCanvas.getContext('2d', { alpha: false  });
    if (!draw2D)
      return;

    if (!size)
      return;

    const rect = destinationCanvas.getBoundingClientRect();
    destinationCanvas.width = rect.width;
    destinationCanvas.height = rect.height;

    const draw = () => {
      const destinationSize = { x: destinationCanvas.width, y: destinationCanvas.height };

      renderer.setScissor(0, 0, destinationSize.x, destinationSize.y);
      renderer.setViewport(0, 0, destinationSize.x, destinationSize.y);


      camera.left = size.length() * .5;
      camera.right = size.length() * -.5;
      camera.top = size.length() * .5;
      camera.bottom = size.length() * -.5;

      camera.position.set(size.length() * 4, size.length() * 4, size.length() * 4);
      camera.lookAt(new three.Vector3(0, 0, 0));

      camera.updateProjectionMatrix();
      // camera.aspe

      renderer.render(scene, camera);

      draw2D.drawImage(sourceCanvas,
        0, sourceCanvas.height - destinationCanvas.height,
          destinationCanvas.width, destinationCanvas.height,
        0, 0,
          destinationCanvas.width, destinationCanvas.height
      )

      renderer.setScissor(0, 0, sourceCanvas.clientWidth, sourceCanvas.clientHeight);
      renderer.setViewport(0, 0, sourceCanvas.clientWidth, sourceCanvas.clientHeight);

      renderer.clear()

      logger.log(`Redrew snapshot for ${object.name}`)
    }
    
    // requestAnimationFrame(draw);

    j.add(render.pipe(first()).subscribe(draw))

    
  }, [graphics, object, box, size])

  

  return [
    h(html.canvas, { className: classes.snapshotCanvas, ref: previewCanvasRef }),

    // Preview Logic
    h(primitiveNodeTypes.null, {}, h(ThreeJSRoot, {}, [
      h(a3.scene, { ref: previewSceneRef, background: new three.Color('white') }, [
        !!object && [
          h(ObjectClone, { target: object, position: new three.Vector3(0, 0, 0) }),
          !!showBoundingBox && !!box && h(a3.Box3Helper, { box, color: new three.Color('red') }),
        ],
        h(a3.ambientLight, { color: new three.Color('white') }),
        children,
        !!size && h(a3.orthographicCamera, {
          ref: previewCameraRef,
        })
      ]),
    ]))
  ];
};
